import { type CSSProperties, forwardRef } from 'react';

import { createPolymorphicComponent, Flex as MantineFlex, type FlexProps as MantineFlexProps } from '@mantine/core';

import { omitUndefined } from '@/new_src/view/components/utils/omitUndefined';
import { resolveColorTokens } from '@/new_src/view/components/utils/resolveColorTokens';

type ExtraStyleProps = {
  shrink?: CSSProperties['flexShrink'];
  radius?: CSSProperties['borderRadius'];
  boxSizing?: CSSProperties['boxSizing'];
  overflow?: CSSProperties['overflow'];
  overflowY?: CSSProperties['overflowY'];
  overflowX?: CSSProperties['overflowX'];
  zIndex?: CSSProperties['zIndex'];
  alignSelf?: CSSProperties['alignSelf'];
  transform?: CSSProperties['transform'];
  pointerEvents?: CSSProperties['pointerEvents'];
  shadow?: CSSProperties['boxShadow'];
  cursor?: CSSProperties['cursor'];
  bdt?: CSSProperties['borderTop'];
  bdb?: CSSProperties['borderBottom'];
  bdr?: CSSProperties['borderRight'];
  transition?: CSSProperties['transition'];
  textOverflow?: CSSProperties['textOverflow'];
  whiteSpace?: CSSProperties['whiteSpace'];
  wordBreak?: CSSProperties['wordBreak'];
};

export type FlexProps = MantineFlexProps & ExtraStyleProps;

const _Flex = forwardRef<HTMLDivElement, FlexProps>(
  (
    {
      shrink,
      radius,
      boxSizing,
      overflow,
      overflowY,
      overflowX,
      zIndex,
      alignSelf,
      transform,
      pointerEvents,
      shadow,
      cursor,
      bdt,
      bdb,
      bdr,
      transition,
      textOverflow,
      whiteSpace,
      wordBreak,
      style,
      ...rest
    },
    ref
  ) => {
    return (
      <MantineFlex
        ref={ref}
        style={[
          omitUndefined({
            flexShrink: shrink,
            borderRadius: radius,
            boxSizing,
            overflow,
            overflowY,
            overflowX,
            zIndex,
            alignSelf,
            transform,
            pointerEvents,
            boxShadow: shadow,
            cursor,
            borderTop: typeof bdt === 'string' ? resolveColorTokens(bdt) : bdt,
            borderBottom: typeof bdb === 'string' ? resolveColorTokens(bdb) : bdb,
            borderRight: typeof bdr === 'string' ? resolveColorTokens(bdr) : bdr,
            transition,
            textOverflow,
            whiteSpace,
            wordBreak,
          }),
          style,
        ]}
        {...rest}
      />
    );
  }
);

/** @NoneHeadlessComponent */
export const Flex = createPolymorphicComponent<'div', FlexProps>(_Flex);
